"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
	CircleUserRound,
	Gift,
	Menu,
	// PartyPopper,
	Search,
	ShoppingCart,
	X,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/components/auth/auth-context";
import { useCart } from "@/components/cart/cart-context";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useNavbarStore } from "@/lib/store/navbar-store";
import { useSearchStore } from "@/lib/store/search-store";
import { cn } from "@/lib/utils";

interface NavLink {
	href: string;
	label: string;
	badge?: string;
}

const navLinks: NavLink[] = [
	{ href: "/", label: "Home" },
	{ href: "/build-a-box", label: "Build-A-Box" },
	//{ href: "/fbb", label: "IAM X FBB" },
	// Disabled: page removed
	// { href: "/swag", label: "Swag" },
	// Disabled: page removed
	// { href: "/spices", label: "Spices" },
	{ href: "/blog", label: "Blog" },
	{ href: "/about", label: "About" },
	{ href: "/contact", label: "Contact" },
];

export function Navbar() {
	const isMenuOpen = useNavbarStore((s) => s.isMenuOpen);
	const isScrolled = useNavbarStore((s) => s.isScrolled);
	const toggleMenu = useNavbarStore((s) => s.toggleMenu);
	const closeMenu = useNavbarStore((s) => s.closeMenu);
	const setScrolled = useNavbarStore((s) => s.setScrolled);
	const openSearch = useSearchStore((s) => s.openSearch);

	const { cartBadgeCount } = useCart();
	const displayCartCount =
		cartBadgeCount > 99 ? "99+" : cartBadgeCount.toString();
	const { user: authUser } = useAuth();
	const isLoggedIn = authUser.isAuthenticated;
	const pathname = usePathname();

	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const navRef = useRef<HTMLElement>(null);
	const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isMounted, setIsMounted] = useState(false);

	// Transparent on home when at the top, solid after scrolling
	const isHome = pathname === "/";
	const isTransparent = isHome && !isScrolled;

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 20);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [setScrolled]);

	useEffect(() => {
		return () => {
			if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
		};
	}, []);

	const handleMouseEnter = useCallback((index: number) => {
		if (leaveTimeout.current) {
			clearTimeout(leaveTimeout.current);
			leaveTimeout.current = null;
		}
		setHoveredIndex(index);
	}, []);

	const handleMouseLeave = useCallback(() => {
		leaveTimeout.current = setTimeout(() => {
			setHoveredIndex(null);
		}, 80);
	}, []);

	const handleNavLeave = useCallback(() => {
		if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
		setHoveredIndex(null);
	}, []);

	// Positioning is handled by the sticky wrapper in layout.tsx.
	// Background is transparent on home-page top, solid once scrolled.
	const headerClass = cn(
		"w-full transition-all duration-300",
		isTransparent
			? "bg-transparent border-transparent"
			: "bg-background/95 backdrop-blur-md border-b border-border",
	);

	return (
		<header className={cn(headerClass, "relative")}>
			<div className="container mx-auto flex h-16 items-center justify-between px-4">
				<Link href="/" className="flex items-center gap-2">
					<span className="text-xl font-bold tracking-tight text-foreground">
						Ice Age Meals
					</span>
				</Link>

				<nav
					ref={navRef}
					className="hidden md:flex items-center gap-1"
					onMouseLeave={handleNavLeave}
				>
					{navLinks.map((link, index) => {
						const isActive = pathname === link.href;
						const isHovered = index === hoveredIndex;
						return (
							<Link
								key={link.href}
								href={link.href}
								className="relative px-4 py-2 text-sm font-medium flex items-center gap-1 rounded-full"
								onMouseEnter={() => handleMouseEnter(index)}
								onMouseLeave={handleMouseLeave}
							>
								<span
									className={cn(
										"relative z-10 transition-colors duration-200",
										isHovered
											? "text-background"
											: isActive
												? "text-primary"
												: "text-foreground",
									)}
								>
									{link.label}
								</span>
								{link.badge && (
									<span
										className={cn(
											"relative z-10 ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold shadow-sm transition-colors duration-200",
											isHovered
												? "bg-background text-foreground"
												: "bg-primary text-primary-foreground",
										)}
									>
										{link.badge}
									</span>
								)}
								{isHovered && (
									<motion.div
										layoutId="navbar-hover-pill"
										className="absolute inset-0 rounded-full bg-foreground"
										style={{ originY: "center" }}
										transition={{
											type: "spring",
											stiffness: 350,
											damping: 30,
											mass: 0.8,
										}}
									/>
								)}
								{isActive && !isHovered && (
									<motion.div
										layoutId="navbar-active-indicator"
										className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-6 rounded-full bg-primary"
										transition={{
											type: "spring",
											stiffness: 300,
											damping: 25,
										}}
									/>
								)}
							</Link>
						);
					})}
				</nav>

				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						onClick={openSearch}
						className="inline-flex text-foreground hover:bg-muted hover:text-foreground transition-colors rounded-full"
						aria-label="Search"
					>
						<Search className="h-5 w-5" />
					</Button>
					<div className="hidden md:flex items-center gap-1">
						{isMounted && isLoggedIn ? (
							<Button
								asChild
								size="sm"
								variant="default"
								className="font-medium transition-opacity hover:opacity-90 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-9 pl-1 pr-4"
							>
								<Link
									href="https://account.geticeagemeals.com"
									prefetch={false}
									className="flex items-center gap-2"
								>
									<Avatar className="h-7 w-7 border-2 border-primary-foreground/20">
										<AvatarFallback className="bg-primary/20 text-primary-foreground text-xs">
											<CircleUserRound className="h-3.5 w-3.5" />
										</AvatarFallback>
									</Avatar>
									<span>My Account</span>
								</Link>
							</Button>
						) : (
							<Button
								asChild
								size="sm"
								variant="default"
								className="font-medium transition-opacity hover:opacity-90 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
							>
								<Link href="/api/auth/login" prefetch={false}>
									Sign In
								</Link>
							</Button>
						)}
					</div>

					{/*
					<Button
						asChild
						variant="ghost"
						size="icon"
						className="relative text-foreground hover:bg-muted hover:text-foreground transition-colors rounded-full"
					>
						<Link
							href="https://af.uppromote.com/ice-age-meals/register"
							target="_blank"
							rel="noopener noreferrer"
						>
							<PartyPopper className="h-5 w-5" />
							<span className="sr-only">Rewards</span>
						</Link>
					</Button>
					*/}

					<Button
						asChild
						variant="ghost"
						size="icon"
						className="relative text-foreground hover:bg-muted hover:text-foreground transition-colors rounded-full"
					>
						<Link href="/gift-cards">
							<Gift className="h-5 w-5" />
							<span className="sr-only">Gift Cards</span>
						</Link>
					</Button>

					<Button
						asChild
						variant="ghost"
						size="icon"
						className="relative text-foreground hover:bg-muted hover:text-foreground transition-colors rounded-full"
					>
						<Link href="/cart">
							<ShoppingCart className="h-5 w-5" />
							{isMounted && cartBadgeCount > 0 && (
								<span className="absolute -top-1.5 -right-1.5 flex min-w-5 h-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
									{displayCartCount}
								</span>
							)}
							<span className="sr-only">Cart</span>
						</Link>
					</Button>

					<AnimatedThemeToggler className="h-10 w-10 p-2 text-foreground hover:bg-muted hover:text-foreground transition-colors rounded-full flex items-center justify-center" />
					<Button
						variant="ghost"
						size="icon"
						className="md:hidden"
						onClick={toggleMenu}
					>
						{isMenuOpen ? (
							<X className="h-5 w-5" />
						) : (
							<Menu className="h-5 w-5" />
						)}
						<span className="sr-only">Toggle menu</span>
					</Button>
				</div>
			</div>

			<AnimatePresence>
				{isMenuOpen && (
					<motion.div
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -8 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
						className="md:hidden absolute left-0 right-0 top-full z-50 bg-background/95 backdrop-blur-md border-t border-border shadow-lg"
					>
						<div className="container mx-auto px-4 py-4 space-y-4">
							<nav className="flex flex-col gap-1">
								{navLinks.map((link, index) => {
									const isActive = pathname === link.href;
									return (
										<motion.div
											key={link.href}
											initial={{ opacity: 0, x: -10 }}
											animate={{ opacity: 1, x: 0 }}
											transition={{ delay: index * 0.05, duration: 0.2 }}
										>
											<Link
												href={link.href}
												onClick={closeMenu}
												className={cn(
													"block py-2.5 px-3 text-sm font-medium rounded-lg transition-colors",
													isActive
														? "bg-primary/10 text-primary"
														: "text-muted-foreground hover:text-foreground hover:bg-muted/50",
												)}
											>
												{link.label}
												{link.badge && (
													<span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-primary text-primary-foreground">
														{link.badge}
													</span>
												)}
											</Link>
										</motion.div>
									);
								})}
							</nav>

							{isMounted && isLoggedIn ? (
								<div className="pt-2 border-t border-border">
									<Link
										href="https://account.geticeagemeals.com"
										className="block"
										onClick={closeMenu}
										prefetch={false}
									>
										<Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 flex gap-2">
											<Avatar className="h-6 w-6 border border-primary-foreground/20">
												<AvatarFallback className="bg-primary/20 text-primary-foreground text-[10px]">
													<CircleUserRound className="h-3 w-3" />
												</AvatarFallback>
											</Avatar>
											My account
										</Button>
									</Link>
								</div>
							) : (
								<div className="pt-2 border-t border-border">
									<Link
										href="/api/auth/login"
										className="block"
										onClick={closeMenu}
										prefetch={false}
									>
										<Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
											Sign In
										</Button>
									</Link>
								</div>
							)}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
}
